/**
 * IncomeSheet - plan_26, plan_28 (every theme).
 *
 * plan_28: a Coming in / Going out switch. Money going out is something you
 * already know you'll spend from your funds (a trip, a repair). It's saved
 * with kind: 'out' and taken off projected amounts, never current balances.
 * Funds can show what they have now (`balance`).
 *
 * The slide-up sheet for adding or editing a future income payment. You type
 * the total coming in first, then hand it out to funds and investments.
 * "Left to allocate" sits under the total and stays pinned while you scroll,
 * counting down as you type. An amount means a fund is included; an empty box
 * means it isn't. "+ Rest" puts whatever's left into that one.
 *
 * Saves { description, expected_date, amount (what's handed out),
 * planned_total (what you typed as the total), fund_allocations,
 * investment_allocations } and, when editing, moved_to_account /
 * bonus_received from the one "Moved into my accounts" tick.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatCurrency, formatCurrencyWhole, getCurrentCurrency } from "../../utils/formatters";

const num = (v) => {
  const n = parseFloat(String(v == null ? '' : v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};
const clean = (v) => String(v).replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
// $1,200 when it's whole dollars, $1,200.50 when it isn't.
const money = (v) => (Math.abs(v - Math.round(v)) < 0.005 ? formatCurrencyWhole(Math.round(v)) : formatCurrency(v));
const toText = (v) => (v == null || v === '' || num(v) === 0 ? '' : String(Math.round(num(v) * 100) / 100));

// Colours for each theme. `calm` is the Money plan theme.
const LOOKS = {
  calm: { title: { fontSize: 20, fontWeight: 600, letterSpacing: '-0.3px' }, label: { fontSize: 12, color: '#8B879A' }, sec: { fontSize: 13, fontWeight: 600, color: '#8B879A' }, field: '#F4F1FB', border: 'transparent', ink: '#26252E', soft: '#ECE6FA', accent: '#7A5DD3', ring: '#A58BE6', save: '#1F1E24', saveRadius: 999, x: '#F4F1FB', xInk: '#8B879A' },
  pink: { serif: true, field: '#FAF8FD', border: '#F2D9E8', ink: '#1F2937', soft: '#F4EEFB', accent: '#7B5EA7', total: '#C23A72', totalBg: '#FDF0F8', ring: '#E27BA1', save: 'linear-gradient(135deg, #F5A8C1 0%, #E27BA1 100%)', saveRadius: 14, x: '#FDF0F8', xInk: '#C23A72' },
  purple: { serif: true, field: '#FAF8FD', border: '#EADFF3', ink: '#1F2937', soft: '#F4EEFB', accent: '#7B5EA7', total: '#8B5CE0', totalBg: '#F7F0FC', ring: '#AC8DF2', save: '#AC8DF2', saveRadius: 14, x: '#F7F0FC', xInk: '#8B5CE0' },
  warm: { serif: true, field: '#FAF8F4', border: '#E3D9C6', ink: '#1F2937', soft: '#EDF4EE', accent: '#2D5A3D', total: '#2D5A3D', totalBg: '#EDF4EE', ring: '#5A9E78', save: '#5A9E78', saveRadius: 14, x: '#EDF4EE', xInk: '#2D5A3D' },
  blue: { serif: true, field: '#F8F9FF', border: '#D9DEF7', ink: '#1F2937', soft: '#EEF0FF', accent: '#4361EE', total: '#4361EE', totalBg: '#EEF0FF', ring: '#6381F0', save: '#6381F0', saveRadius: 14, x: '#EEF0FF', xInk: '#4361EE' },
};

/**
 * payment: the entry being edited, or null to add a new one.
 * funds: [{ id, name, emoji }], investments: [{ id, name, emoji }]
 * tint(i): { wash, bar|mid } for the i-th fund, bar colours
 * look: 'calm' | 'pink' | 'purple' | 'warm' | 'blue'
 */
export default function IncomeSheet({ payment = null, funds = [], investments = [], tint, look = 'pink', onSave, onDelete, onClose }) {
  const L = LOOKS[look] || LOOKS.pink;
  const calm = look === 'calm';
  const editing = !!payment;
  const [description, setDescription] = useState(payment ? payment.description || '' : '');
  const [date, setDate] = useState(payment ? payment.expected_date || '' : '');
  const startAlloc = (obj) => Object.fromEntries(Object.entries(obj || {}).map(([k, v]) => [k, toText(v)]).filter(([, v]) => v !== ''));
  const [fundAmts, setFundAmts] = useState(() => startAlloc(payment && payment.fund_allocations));
  const [invAmts, setInvAmts] = useState(() => startAlloc(payment && payment.investment_allocations));
  const given0 = useMemo(() => Object.values(fundAmts).concat(Object.values(invAmts)).reduce((s, v) => s + num(v), 0), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [total, setTotal] = useState(() => toText(payment ? (payment.planned_total != null ? payment.planned_total : given0) : ''));
  const [moved, setMoved] = useState(!!(payment && payment.moved_to_account));
  const [kind, setKind] = useState(payment && payment.kind === 'out' ? 'out' : 'in'); // plan_28
  const out = kind === 'out';
  const [note, setNote] = useState(payment && payment.note ? String(payment.note) : ''); // plan_30
  const [kb, setKb] = useState(false);
  const [error, setError] = useState('');
  const sheetRef = useRef(null);

  const given = Object.values(fundAmts).concat(out ? [] : Object.values(invAmts)).reduce((s, v) => s + num(v), 0);
  const totalN = num(total);
  const left = Math.round((totalN - given) * 100) / 100;
  const state = !totalN ? 'none' : left > 0.004 ? 'left' : left < -0.004 ? 'over' : 'done';
  const box = state === 'done'
    ? { bg: '#DFF2E7', ink: '#2F8756', text: out ? 'All covered ✓' : 'All allocated ✓', value: money(0) }
    : state === 'over'
    ? { bg: '#FAE4ED', ink: '#C04B7C', text: out ? 'Taking too much by' : 'Over by', value: money(-left) }
    : state === 'left'
    ? (out ? { bg: '#FAE4ED', ink: '#C04B7C', text: 'Still to take from a fund', value: money(left) } : { bg: L.soft, ink: L.accent, text: 'Left to allocate', value: money(left) })
    : { bg: L.soft, ink: L.accent, text: out ? 'Taken from funds so far' : 'Handed out so far', value: money(given) };

  // Bar: each amount in its fund's colour, out of the total.
  const segs = [];
  funds.forEach((f, i) => { const v = num(fundAmts[f.id]); if (v > 0) { const t = tint ? tint(i) : null; segs.push({ k: 'f' + f.id, v, c: (t && (t.bar || t.mid)) || '#B9A6F0' }); } });
  if (!out) investments.forEach((a, i) => { const v = num(invAmts[a.id]); if (v > 0) { const t = tint ? tint(funds.length + i) : null; segs.push({ k: 'i' + a.id, v, c: (t && (t.bar || t.mid)) || '#A9C6F0' }); } });
  const denom = Math.max(totalN, given, 1);

  // Room for the keyboard: scroll the box you're typing in into view.
  useEffect(() => {
    const el = sheetRef.current;
    if (!el) return undefined;
    let off = null;
    const onIn = (e) => {
      if (!/^(INPUT|TEXTAREA)$/.test(e.target.tagName)) return;
      clearTimeout(off);
      setKb(true);
      setTimeout(() => { try { e.target.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (err) { /* older WebKit */ } }, 320);
    };
    const onOut = () => { clearTimeout(off); off = setTimeout(() => { const a = document.activeElement; if (!(a && el.contains(a) && /^(INPUT|TEXTAREA)$/.test(a.tagName))) setKb(false); }, 150); };
    el.addEventListener('focusin', onIn);
    el.addEventListener('focusout', onOut);
    return () => { clearTimeout(off); el.removeEventListener('focusin', onIn); el.removeEventListener('focusout', onOut); };
  }, []);

  const save = () => {
    const fa = {}; const ia = {};
    Object.entries(fundAmts).forEach(([k, v]) => { const n = num(v); if (n > 0) fa[k] = n; });
    if (!out) Object.entries(invAmts).forEach(([k, v]) => { const n = num(v); if (n > 0) ia[k] = n; });
    const amount = Object.values(fa).concat(Object.values(ia)).reduce((s, n) => s + n, 0);
    if (!description.trim()) { setError(out ? 'Add what this is for.' : 'Add what this income is.'); return; }
    if (amount <= 0) { setError(out ? 'Say which fund it comes out of.' : 'Give at least one fund or account an amount.'); return; }
    const entry = {
      description: description.trim(),
      expected_date: date || new Date().toISOString().split('T')[0],
      amount,
      planned_total: totalN > 0 ? totalN : amount,
      fund_allocations: fa,
      investment_allocations: ia,
      kind,
      note: note.trim(),
    };
    if (editing) { entry.moved_to_account = moved; entry.bonus_received = moved ? true : !!payment.bonus_received; }
    onSave(entry);
  };

  const sym = getCurrentCurrency().symbol;
  const fieldStyle = { background: L.field, border: `1px solid ${L.border}`, borderRadius: calm ? 16 : 12, padding: '0 13px', height: 48, width: '100%', boxSizing: 'border-box', fontSize: 16, color: L.ink, outline: 'none', fontFamily: 'inherit' };
  const labelStyle = calm
    ? { display: 'block', fontSize: 12, color: '#8B879A', margin: '0 2px 5px' }
    : { display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: L.accent, margin: '0 0 5px' };
  const secStyle = calm
    ? { fontSize: 13, fontWeight: 600, color: '#8B879A', margin: '16px 2px 4px' }
    : { fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#A39FA0', margin: '16px 0 4px' };

  // A plain function (not a component), so the box you're typing in keeps focus.
  const row = ({ id, e, name, value, setAll, i, isInv, has }) => {
    const t = tint ? tint(isInv ? funds.length + i : i) : null;
    const empty = !num(value);
    return (
      <div key={(isInv ? 'i' : 'f') + id} className="flex items-center" style={{ gap: 11, padding: '8px 0', borderTop: i ? `1px solid ${calm ? '#EEEAF6' : '#F3EEF6'}` : 0 }}>
        <span className="flex items-center justify-center flex-shrink-0" style={{ width: 38, height: 38, borderRadius: 13, background: calm && t ? t.wash : 'transparent', fontSize: 18 }}>{e}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="truncate" style={{ display: 'block', fontSize: 14.5, fontWeight: 500, color: L.ink }}>{name}</span>
          {out && has != null && <span style={{ display: 'block', fontSize: 11.5, color: '#8B879A', marginTop: 1 }}>Has {money(has)} now</span>}
        </span>
        {empty && state === 'left' && (
          <button type="button" onClick={() => setAll((m) => ({ ...m, [id]: toText(left) }))} style={{ flexShrink: 0, border: 0, borderRadius: 999, padding: '4px 9px', fontSize: 11.5, fontWeight: 600, color: calm ? '#7A5DD3' : (L.total || L.accent), background: calm ? '#ECE6FA' : (L.totalBg || L.soft) }}>＋ Rest</button>
        )}
        <span className="flex items-center flex-shrink-0" style={{ width: 104, background: calm ? L.field : '#FFFFFF', border: calm ? 0 : `1px solid ${L.border}`, borderRadius: 13, padding: '0 10px', height: 40, boxSizing: 'border-box' }}>
          <span style={{ fontSize: 13, color: '#B4AFC2', marginRight: 2 }}>{sym}</span>
          <input
            type="text"
            inputMode="decimal"
            placeholder="0"
            value={value || ''}
            onChange={(ev) => { const v = clean(ev.target.value); setAll((m) => { const n = { ...m }; if (v === '') delete n[id]; else n[id] = v; return n; }); }}
            style={{ width: '100%', minWidth: 0, border: 0, background: 'transparent', textAlign: 'right', fontSize: 15, fontWeight: 600, color: L.ink, outline: 'none', fontFamily: 'inherit', padding: 0 }}
          />
        </span>
      </div>
    );
  };

  return createPortal(
    <div className="fixed inset-0" style={{ zIndex: 60, touchAction: 'none' }} data-patch="plan_26">
      <div className="absolute" style={{ inset: '-60px 0', background: 'rgba(0,0,0,0.4)' }} onClick={onClose} />
      <div
        ref={sheetRef}
        className="absolute bottom-0 left-0 right-0 w-full flex flex-col"
        style={{ background: '#FFFFFF', borderRadius: '28px 28px 0 0', maxHeight: '90vh', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)', boxShadow: '0 -8px 30px rgba(40,30,80,0.10)', fontFamily: calm ? undefined : "'DM Sans', sans-serif", touchAction: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex justify-center flex-shrink-0"
          style={{ padding: '10px 0 6px' }}
          onTouchStart={(e) => { e.currentTarget._y = e.touches[0].clientY; }}
          onTouchMove={(e) => { const dy = e.touches[0].clientY - e.currentTarget._y; if (dy > 0) e.currentTarget.parentElement.style.transform = `translateY(${dy}px)`; }}
          onTouchEnd={(e) => {
            const dy = e.changedTouches[0].clientY - e.currentTarget._y;
            const el = e.currentTarget.parentElement;
            if (dy > 60) { el.style.transform = ''; onClose(); } else { el.style.transition = 'transform .2s'; el.style.transform = ''; setTimeout(() => { el.style.transition = ''; }, 200); }
          }}
        >
          <div style={{ width: 40, height: 4, borderRadius: 9, background: '#DDD8E8' }} />
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain" style={{ padding: '0 18px', paddingBottom: kb ? '45vh' : 8 }}>
          <div className="flex items-center justify-between" style={{ margin: '2px 0 12px' }}>
            <span style={L.serif ? { fontFamily: "'DM Serif Display', serif", fontSize: 24, color: '#1A1A1F' } : { fontSize: 20, fontWeight: 600, letterSpacing: '-0.3px', color: L.ink }}>
              {out
                ? (editing ? (L.serif ? 'Edit Spending' : 'Edit spending') : (L.serif ? 'Plan Spending' : 'Plan spending'))
                : (editing ? (L.serif ? 'Edit Income' : 'Edit income') : (L.serif ? 'Add Income' : 'Add income'))}
            </span>
            <button type="button" aria-label="Close" onClick={onClose} className="flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 999, border: 0, background: L.x, color: L.xInk, fontSize: 14 }}>✕</button>
          </div>

          {/* plan_28: coming in or going out */}
          <div className="flex" style={{ background: calm ? L.field : '#F3F1F6', borderRadius: 999, padding: 3, marginBottom: 6 }}>
            {[['in', 'Coming in'], ['out', 'Going out']].map(([k, l]) => (
              <button key={k} type="button" onClick={() => { setKind(k); setError(''); }} style={{ flex: 1, border: 0, borderRadius: 999, padding: '9px 4px', fontSize: 14, fontWeight: kind === k ? 600 : 500, color: kind === k ? L.ink : '#6C6879', background: kind === k ? '#FFFFFF' : 'transparent', boxShadow: kind === k ? '0 1px 3px rgba(40,30,80,0.08)' : 'none', fontFamily: 'inherit' }}>{l}</button>
            ))}
          </div>
          <p style={{ margin: '0 4px 10px', fontSize: 12.5, color: '#8B879A', lineHeight: 1.45 }}>
            {out ? "Something you already know you'll spend from your funds, like a trip or a repair. It comes off your projected amounts, never your current balance." : 'Money you expect, like a bonus or a tax refund, and where it will go.'}
          </p>
          <div className="grid" style={{ gridTemplateColumns: '1.25fr 1fr', gap: 8 }}>
            <label style={{ minWidth: 0 }}>
              <span style={labelStyle}>{out ? (calm ? 'What for?' : 'Description') : (calm ? 'What is it?' : 'Description')}</span>
              <input type="text" value={description} placeholder={out ? 'e.g. December trip' : 'e.g. Tax refund'} onChange={(e) => { setDescription(e.target.value); setError(''); }} style={fieldStyle} />
            </label>
            <label style={{ minWidth: 0 }}>
              <span style={labelStyle}>{calm ? (out ? 'When' : 'Expected') : (out ? 'Date' : 'Expected date')}</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...fieldStyle, WebkitAppearance: 'none', appearance: 'none', lineHeight: '48px', padding: '0 10px' }} />
            </label>
          </div>

          {/* The total and what's left stay pinned at the top while you scroll. */}
          <div style={{ position: 'sticky', top: 0, zIndex: 2, background: '#FFFFFF', paddingTop: 8, paddingBottom: 4 }}>
            <label className="flex items-center justify-between" style={{ background: calm ? L.field : L.totalBg, borderRadius: calm ? 20 : 16, padding: '10px 14px', gap: 10 }}>
              <span style={calm ? { fontSize: 12.5, color: '#8B879A' } : { fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: L.total }}>{out ? (calm ? 'Going out' : 'Total going out') : (calm ? 'Coming in' : 'Total coming in')}</span>
              <span className="flex items-baseline" style={{ minWidth: 0, flex: 1, justifyContent: 'flex-end' }}>
                <span style={{ fontSize: 20, color: calm ? '#8B879A' : L.total, marginRight: 2, fontFamily: L.serif ? "'DM Serif Display', serif" : undefined }}>{sym}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  value={total}
                  onChange={(e) => setTotal(clean(e.target.value))}
                  style={{ width: Math.max(2, String(total || '0').length + 1) + 'ch', minWidth: 0, maxWidth: 170, border: 0, background: 'transparent', textAlign: 'right', fontSize: L.serif ? 28 : 24, fontWeight: L.serif ? 400 : 600, letterSpacing: '-0.5px', color: calm ? L.ink : L.total, outline: 'none', padding: 0, fontFamily: L.serif ? "'DM Serif Display', serif" : 'inherit' }}
                />
              </span>
            </label>
            <div style={{ background: box.bg, borderRadius: calm ? 20 : 16, padding: '11px 14px', marginTop: 8 }}>
              <div className="flex items-baseline justify-between">
                <span style={{ fontSize: 13, fontWeight: 600, color: box.ink }}>{box.text}</span>
                <span style={{ fontSize: 18, fontWeight: 600, color: box.ink, letterSpacing: '-0.3px' }}>{box.value}</span>
              </div>
              <div className="flex" style={{ gap: 3, height: 8, borderRadius: 9, overflow: 'hidden', background: state === 'over' ? '#F2AFC8' : 'rgba(255,255,255,0.8)', marginTop: 8 }}>
                {state !== 'over' && segs.map((s) => <i key={s.k} style={{ display: 'block', height: '100%', width: (s.v / denom * 100) + '%', background: s.c }} />)}
              </div>
              {state === 'none' && <span style={{ display: 'block', fontSize: 11.5, color: box.ink, opacity: 0.8, marginTop: 6 }}>Type the total above to see what's left.</span>}
              {state === 'over' && <span style={{ display: 'block', fontSize: 11.5, color: box.ink, marginTop: 6 }}>You've handed out {money(given)} of {money(totalN)}.</span>}
            </div>
          </div>

          <p style={secStyle}>{out ? 'Comes out of' : calm ? 'Funds' : 'Sinking funds'}</p>
          {funds.map((f, i) => row({ id: f.id, i, e: f.emoji || '\u{1F4B0}', name: f.name, value: fundAmts[f.id], setAll: setFundAmts, has: f.balance }))}
          {!out && investments.length > 0 && <p style={secStyle}>Investments</p>}
          {!out && investments.map((a, i) => row({ id: a.id, i, isInv: true, e: a.emoji || '\u{1F4CA}', name: a.name, value: invAmts[a.id], setAll: setInvAmts }))}

          {editing && (
            <button type="button" onClick={() => setMoved((v) => !v)} className="flex items-center w-full text-left" style={{ gap: 10, marginTop: 14, background: calm ? L.field : L.soft, border: 0, borderRadius: 16, padding: '12px 14px' }}>
              <span className="flex items-center justify-center flex-shrink-0" style={{ width: 22, height: 22, borderRadius: 999, border: `2px solid ${moved ? (calm ? '#1F1E24' : L.accent) : '#CFC7E6'}`, background: moved ? (calm ? '#1F1E24' : L.accent) : 'transparent', color: '#fff', fontSize: 12 }}>{moved ? '✓' : ''}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: L.ink }}>{out ? "It's been spent" : 'Moved into my accounts'}</span>
                <span style={{ display: 'block', fontSize: 12, color: '#8B879A', marginTop: 1 }}>{out ? "It stops counting in projections, since it's out of your balances now." : "It stops counting in projections, since it's in your balances now."}</span>
              </span>
            </button>
          )}

          {/* plan_30: an optional note */}
          <label style={{ display: 'block', marginTop: 14 }}>
            <span style={labelStyle}>Note (optional)</span>
            <textarea
              value={note}
              placeholder={out ? 'e.g. Flights plus two weeks of spending' : 'e.g. Only if the full bonus comes through'}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              style={{ ...fieldStyle, height: 88, padding: '11px 13px', fontSize: 16, lineHeight: '22px', resize: 'none', display: 'block', overflowY: 'auto', WebkitAppearance: 'none' }} /* plan_31: steady height keeps the cursor on your line */
            />
          </label>

          {error && <p style={{ margin: '12px 2px 0', fontSize: 13, fontWeight: 600, color: '#C04B7C' }}>{error}</p>}
          <button type="button" onClick={save} className="w-full" style={{ marginTop: 16, height: 52, border: 0, borderRadius: L.saveRadius, background: L.save, color: '#FFFFFF', fontSize: 15, fontWeight: 600 }}>
            {editing ? (L.serif ? 'Save Changes' : 'Save changes') : out ? 'Save' : (L.serif ? 'Save Income' : 'Save income')}
          </button>
          {state === 'left' && <p style={{ margin: '8px 0 0', fontSize: 12.5, color: '#8B879A', textAlign: 'center' }}>{out ? 'You can save with some left. Only the amounts you take from funds count.' : 'You can save with some left. Only the amounts you hand out count.'}</p>}
          {editing && onDelete && (
            <button type="button" onClick={onDelete} className="w-full" style={{ marginTop: 10, border: 0, background: 'none', fontSize: 14, fontWeight: 600, color: '#C04B7C', padding: 8 }}>{out ? 'Delete this' : 'Delete this income'}</button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
